/** Epic 251 FV-2 — field verification walkdown domain helpers. */

export const FIELD_VERIFICATION_ITEM_KEYS = [
  'camera_zone_match',
  'floor_markings',
  'signage',
  'physical_barrier',
  'route_reviewed',
  'authorized_exceptions',
  'supervisor_handoff',
] as const;

export type FieldVerificationItemKey = (typeof FIELD_VERIFICATION_ITEM_KEYS)[number];

export const FIELD_VERIFICATION_ITEM_LABELS: Record<FieldVerificationItemKey, string> = {
  camera_zone_match: 'Camera zone matches real restricted area',
  floor_markings: 'Floor markings visible at decision point',
  signage: 'Signage present and understandable',
  physical_barrier: 'Physical barrier / gate / separation exists',
  route_reviewed: 'Normal workflow route reviewed',
  authorized_exceptions: 'Authorized exceptions defined',
  supervisor_handoff: 'Supervisor procedure / shift handoff confirmed',
};

export const PHYSICAL_EVIDENCE_REQUIRED_KEYS = new Set<FieldVerificationItemKey>([
  'camera_zone_match',
  'floor_markings',
  'signage',
  'physical_barrier',
]);

export type FieldVerificationDisposition = 'CONFIRMED' | 'FAIL' | 'NOT_APPLICABLE';

export type FieldVerificationItem = {
  id?: string | null;
  item_key: FieldVerificationItemKey | string;
  label?: string;
  disposition?: FieldVerificationDisposition | null;
  note?: string | null;
  evidence_ref?: string | null;
  answered_by?: string | null;
  answered_at?: string | null;
  superseded_by?: string | null;
};

export type FieldVerificationState = 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETE';

export type FieldVerificationSummary = {
  workflow_id?: string;
  items: FieldVerificationItem[];
  answered: number;
  total: number;
  all_answered: boolean;
  any_fail: boolean;
  unanswered_item_keys: string[];
  field_verification_state: FieldVerificationState;
  field_verification_complete: boolean;
  overrides?: Array<{
    id?: string;
    item_key: string;
    reason: string;
    approved_by: string;
    approved_at?: string;
  }>;
};

export function emptyFieldVerificationSummary(workflowId = ''): FieldVerificationSummary {
  return {
    workflow_id: workflowId,
    items: FIELD_VERIFICATION_ITEM_KEYS.map((item_key) => ({
      item_key,
      label: FIELD_VERIFICATION_ITEM_LABELS[item_key],
      disposition: null,
    })),
    answered: 0,
    total: 7,
    all_answered: false,
    any_fail: false,
    unanswered_item_keys: [...FIELD_VERIFICATION_ITEM_KEYS],
    field_verification_state: 'NOT_STARTED',
    field_verification_complete: false,
    overrides: [],
  };
}

/** Placeholder / instruction text operators sometimes type into Evidence ref. */
export function isPlaceholderEvidenceRef(value: string | null | undefined): boolean {
  const text = String(value || '').trim().toLowerCase();
  if (!text) return true;
  return (
    text === 'snapshot/clip id'
    || text === 'snapshot / clip id'
    || text === 'snapshot or clip id'
    || text === 'clip id'
    || text === 'snapshot id'
    || text === 'photo / walkdown id'
    || text === 'evidence ref'
  );
}

/** Build a human evidence_ref from the open review's representative media (no opaque IDs to invent). */
export function suggestFieldVerificationEvidenceRef(input: {
  incidentId?: string | null;
  snapshotFilename?: string | null;
  clipFilename?: string | null;
}): string {
  const incidentId = String(input.incidentId || '').trim();
  const snapshot = String(input.snapshotFilename || '').trim();
  const clip = String(input.clipFilename || '').trim();
  if (snapshot && clip) {
    const scope = incidentId ? ` (${incidentId})` : '';
    return `Snapshot + clip on this page${scope}: ${snapshot} · ${clip}`;
  }
  if (snapshot) {
    const scope = incidentId ? ` (${incidentId})` : '';
    return `Snapshot on this page${scope}: ${snapshot}`;
  }
  if (clip) {
    const scope = incidentId ? ` (${incidentId})` : '';
    return `Clip on this page${scope}: ${clip}`;
  }
  if (incidentId) {
    return `Representative evidence on this page (${incidentId})`;
  }
  return '';
}

export function effectiveFieldVerificationEvidenceRef(
  value: string | null | undefined,
  suggested?: string | null,
): string {
  const raw = String(value || '').trim();
  if (raw && !isPlaceholderEvidenceRef(raw)) return raw;
  return String(suggested || '').trim();
}

export function validateFieldVerificationAnswer(input: {
  item_key: string;
  disposition: FieldVerificationDisposition | string;
  note?: string | null;
  evidence_ref?: string | null;
}): { ok: true } | { ok: false; error: string } {
  const key = String(input.item_key || '').trim() as FieldVerificationItemKey;
  if (!FIELD_VERIFICATION_ITEM_KEYS.includes(key)) {
    return { ok: false, error: 'invalid_item_key' };
  }
  const disposition = String(input.disposition || '').trim().toUpperCase();
  if (!['CONFIRMED', 'FAIL', 'NOT_APPLICABLE'].includes(disposition)) {
    return { ok: false, error: 'invalid_disposition' };
  }
  const note = String(input.note || '').trim();
  const evidence = String(input.evidence_ref || '').trim();
  if ((disposition === 'FAIL' || disposition === 'NOT_APPLICABLE') && !note) {
    return { ok: false, error: 'note_required' };
  }
  if (
    disposition === 'CONFIRMED'
    && PHYSICAL_EVIDENCE_REQUIRED_KEYS.has(key)
    && (!evidence || isPlaceholderEvidenceRef(evidence))
  ) {
    return { ok: false, error: 'evidence_ref_required' };
  }
  return { ok: true };
}

export function fieldVerificationSignoffGate(summary: FieldVerificationSummary | null | undefined): {
  allowed: boolean;
  code: string | null;
  itemKeys: string[];
  customerLabel: string | null;
} {
  if (!summary) {
    return {
      allowed: false,
      code: 'field_verification_unavailable',
      itemKeys: [],
      customerLabel: 'Control confirmation on site required',
    };
  }
  const complete = summary.field_verification_state === 'COMPLETE'
    || summary.field_verification_complete === true
    || summary.all_answered === true;
  if (!complete) {
    return {
      allowed: false,
      code: 'field_verification_incomplete',
      itemKeys: [...(summary.unanswered_item_keys || [])],
      customerLabel: 'Control confirmation on site required',
    };
  }
  const overrideKeys = new Set(
    (summary.overrides || []).map((row) => String(row.item_key || '').trim()).filter(Boolean),
  );
  const failed = (summary.items || [])
    .filter((item) => String(item.disposition || '').toUpperCase() === 'FAIL'
      && !overrideKeys.has(String(item.item_key || '').trim()))
    .map((item) => String(item.item_key || '').trim())
    .filter(Boolean);
  if (failed.length || summary.any_fail) {
    const labels = failed
      .map((key) => FIELD_VERIFICATION_ITEM_LABELS[key as FieldVerificationItemKey] || key);
    return {
      allowed: false,
      code: 'field_verification_failed',
      itemKeys: failed,
      customerLabel: labels.length
        ? `Control confirmation on site failed: ${labels.join(', ')}`
        : 'Control confirmation on site failed',
    };
  }
  return {
    allowed: true,
    code: null,
    itemKeys: [],
    customerLabel: null,
  };
}

export function fieldVerificationProgressLabel(summary: FieldVerificationSummary | null | undefined): string {
  const answered = Number(summary?.answered || 0);
  const total = Number(summary?.total || 7);
  return `Answered ${answered} of ${total} · Closure blocked until all items answered.`;
}

/** Buyer-facing copy after a disposition click fails client validation. */
export function fieldVerificationValidationMessage(
  error: string,
  disposition?: FieldVerificationDisposition | string | null,
): string {
  const code = String(error || '').trim();
  const action = String(disposition || '').trim().toUpperCase();
  if (code === 'note_required') {
    if (action === 'FAIL') {
      return 'Add a short note explaining the fail, then press Fail again.';
    }
    if (action === 'NOT_APPLICABLE') {
      return 'Add a short note explaining why this is N/A, then press N/A again.';
    }
    return 'Add a short note, then press Fail or N/A again.';
  }
  if (code === 'evidence_ref_required') {
    return 'Use the snapshot/clip on this page (or type a walkdown photo ref), then press Confirmed again.';
  }
  return code || 'Could not save this walkdown answer.';
}
